import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
  {
    variants: {
      variant: {
        default: 'border-border bg-muted text-foreground/80',
        success: 'border-success/40 bg-success/10 text-success',
        warning: 'border-warning/40 bg-warning/10 text-warning',
        danger: 'border-destructive/40 bg-destructive/10 text-destructive',
        info: 'border-primary/40 bg-primary/10 text-primary',
        outline: 'border-border bg-transparent text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export function stateVariant(state: string | null | undefined): BadgeProps['variant'] {
  switch ((state ?? '').toLowerCase()) {
    case 'succeeded':
    case 'completed':
    case 'success':
    case 'processed':
      return 'success';
    case 'processing':
    case 'enqueued':
    case 'scheduled':
    case 'awaiting':
      return 'info';
    case 'failed':
    case 'error':
      return 'danger';
    case 'deleted':
    case 'paused':
      return 'warning';
    default:
      return 'default';
  }
}
